import React, { useState, useMemo, useRef } from "react";
import { Document, Page, pdfjs, Outline, Thumbnail } from "react-pdf";
import {
  Box,
  IconButton,
  Toolbar,
  TextField,
  Button,
  Typography,
  Divider,
  Tooltip,
  Drawer,
  AppBar,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  ZoomIn,
  ZoomOut,
  RotateLeft,
  RotateRight,
  Download,
  Search,
  NavigateBefore,
  NavigateNext,
  Menu,
  Close,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { LoadingSpinner } from "../LoadingSpinner";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface DocumentData {
  id: string;
  object: string;
  for_id: string;
  for_type: string;
  type: string;
  mime: string;
  file_name: string;
  path_to_file: string;
  download_url: string;
  expires_in: null;
  meta: null;
  created_at: string;
  updated_at: string;
}

interface PDFViewerProps {
  documentData: DocumentData;
  showOutline?: boolean;
  showThumbnails?: boolean;
  enableTextSelection?: boolean;
  enableAnnotations?: boolean;
  className?: string;
  width?: number;
  height?: number;
  scale?: number;
  onBack?: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  documentData,
  showOutline = true,
  showThumbnails = false,
  enableTextSelection = true,
  enableAnnotations = true,
  className = "",
  width,
  height,
  scale = 1.0,
  onBack,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfScale, setPdfScale] = useState<number>(scale);
  const [rotation, setRotation] = useState<number>(0);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [outline, setOutline] = useState<any>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Get PDF source from documentData
  const pdfSource = documentData.download_url;
  const fileName = documentData.file_name;

  // PDF.js options for enhanced functionality
  const options = useMemo(
    () => ({
      cMapUrl: "/cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "/standard_fonts/",
    }),
    []
  );

  const onDocumentLoadSuccess = ({
    numPages: totalPages,
  }: {
    numPages: number;
  }) => {
    setNumPages(totalPages);
    setLoading(false);
    setError(null);
    pageRefs.current = new Array(totalPages).fill(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
  };

  const onOutlineLoadSuccess = (outline: any) => {
    setOutline(outline);
  };

  const onItemClick = ({ pageNumber }: { pageNumber: number }) => {
    setPageNumber(pageNumber);
    setShowSidebar(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, numPages));
    setPageNumber(validPage);
  };

  const zoomIn = () => {
    setPdfScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setPdfScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setPdfScale(1.0);
  };

  const rotateClockwise = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const rotateCounterClockwise = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const toggleSidebar = () => {
    setShowSidebar((prev) => !prev);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const renderThumbnails = () => {
    const thumbnails = [];
    for (let i = 1; i <= numPages; i++) {
      thumbnails.push(
        <Box
          key={`thumbnail_${i}`}
          onClick={() => goToPage(i)}
          sx={{
            cursor: "pointer",
            border: pageNumber === i ? 2 : 1,
            borderColor: pageNumber === i ? "primary.main" : "divider",
            borderRadius: 1,
            p: 1,
            bgcolor: pageNumber === i ? "primary.50" : "background.paper",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
        >
          <Thumbnail pageNumber={i} scale={0.15} />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 0.5,
              fontWeight: pageNumber === i ? 600 : 400,
            }}
          >
            {i}
          </Typography>
        </Box>
      );
    }
    return thumbnails;
  };

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              onClick={() => window.location.reload()}
              variant="contained"
              color="error"
            >
              Retry
            </Button>
          }
        >
          <Typography variant="h6" gutterBottom>
            Error Loading PDF
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
        {onBack && (
          <Button startIcon={<ArrowBack />} onClick={onBack} variant="outlined">
            Back to Files
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: isFullscreen ? "100vh" : "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        zIndex: isFullscreen ? 9999 : "auto",
      }}
    >
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
        <Viewer renderLoader={() => <LoadingSpinner />} fileUrl={pdfSource} />
      </Worker>
    </Box>
  );
};

export default PDFViewer;
